import React, { FC, useEffect, useState } from "react";
import { Col, Loader, Row } from "taltech-styleguide";
import "@styles/specification.css";
import { GeneralInfoComponent } from "./GeneralInfoComponent";
import { LayoutInfoComponent } from "./LayoutInfoComponent";
import { BuildingInfoComponent } from "./BuildingInfoComponent";
import { ComponentsInfoComponent } from "./ComponentsInfoComponent";
import { buildingConfigurationSample } from "../configurations/sample";
import { specificationSample } from "./sample";
import { Page } from "@components-common/layouts";
import { application } from "../../../_runtime/bootstrap";
import { useUserContext } from "../../../_runtime/iam/session.context";
import { useParams } from "react-router-dom";
import { clone } from "ramda";
import {
  getDataFromFile,
  mapBuildingInfoData,
  mapElementTypesData,
  mapLayoutData,
} from "./_helpers";

export enum CreationTypeEnum {
  RANDOM,
  CONSTANT,
}

const BuildingSpecificationForm: React.FC<any> = ({
  user,
  assignment,
  course,
  configuration,
  configurationJSON,
  specificationJSON,
}) => {
  const generalInfoData = {
    course: course.name,
    student: user.displayName,
    assignment: assignment.title,
    buildingConfig: configuration.name,
    buildingName: configurationJSON.info.name,
    buildingDescription: configurationJSON.info.description,
  };

  const layoutInfoData = mapLayoutData(
    configurationJSON.layout,
    specificationJSON.layout
  );

  const buildingInfoData = mapBuildingInfoData(
    configurationJSON,
    specificationJSON
  );

  const elementTypesInfoData = mapElementTypesData(
    configurationJSON,
    specificationJSON
  ).elementTypes;
  const elementTypesParameters = {
    wallInterior: specificationJSON.elementTypes.wallInterior.parameters,
    wallExterior: specificationJSON.elementTypes.wallExterior.parameters,
    roof: specificationJSON.elementTypes.roof.parameters,
    floor: specificationJSON.elementTypes.floor.parameters,
    floorGround: specificationJSON.elementTypes.floorGround.parameters,
    windowType: specificationJSON.windowType.parameters,
    buildingParameters: specificationJSON.buildingParameters,
  };

  return (
    <Row>
      <Col xs="6">
        <Row className="gap-1 flex-column">
          <Col>
            <GeneralInfoComponent data={generalInfoData} />
          </Col>
          <Col>
            <LayoutInfoComponent data={layoutInfoData} />
          </Col>
        </Row>
      </Col>
      <Col xs="6">
        <Row className="gap-1 flex-column">
          <Col>
            <BuildingInfoComponent
              data={buildingInfoData}
              buildingParameters={specificationJSON.buildingParameters}
            />
          </Col>
          <Col>
            <ComponentsInfoComponent
              data={elementTypesInfoData}
              parameters={elementTypesParameters}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export const JsonViewer: FC<any> = (props) => {
  const {
    assignmentId,
    userId: userFromUrl,
    buildingConfigurationId,
  } = useParams();
  const { userId: userFromContext } = useUserContext();
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<any>(undefined);
  const [course, setCourse] = useState<any>(undefined);
  const [assignment, setAssignment] = useState<any>(undefined);
  const [configuration, setConfiguration] = useState<any>(undefined);
  const [configurationJSON, setConfigurationJSON] = useState<any>(undefined);
  const [specificationJSON, setSpecificationJSON] = useState<any>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const httpClient = application.getHttpClient();

        const [configResponse, userResponse] = await Promise.all([
          httpClient.get(
            `buildingsconfigms/configurations/${buildingConfigurationId}`
          ),
          httpClient.get(`usersms/users/${userFromUrl || userFromContext}`),
        ]);

        const configuration: any = configResponse.body;
        setConfiguration(configuration);
        await getDataFromFile(configuration.url, setConfigurationJSON);

        const specificationsResponse: any = await httpClient.get(
          `buildingsconfigms/specifications?buildingConfigurationId=${buildingConfigurationId}&userId=${
            userFromUrl || userFromContext
          }`
        );
        await getDataFromFile(
          specificationsResponse.body.items[0].specificationUrl,
          setSpecificationJSON
        );

        const user = userResponse.body;
        setUser(user);

        const assignmentResponse = await httpClient.get(
          `coursesms/assignments/${assignmentId}`
        );
        const assignment: any = assignmentResponse.body;
        setAssignment(assignment);

        const courseResponse = await httpClient.get(
          `coursesms/courses/${assignment.courseId}`
        );
        setCourse(courseResponse.body);
      } catch (error: any) {
        setLoading(false);
      }
    };

    if (buildingConfigurationId !== undefined) fetchData();
  }, []);

  useEffect(() => {
    if (
      specificationJSON !== undefined &&
      configurationJSON !== undefined &&
      user !== undefined &&
      assignment !== undefined &&
      course !== undefined
    ) {
      setLoading(false);
    }
  }, [specificationJSON, course]);

  return (
    <Page title={props.title}>
      {!loading ? (
        <BuildingSpecificationForm
          user={user}
          assignment={assignment}
          course={course}
          configuration={configuration}
          configurationJSON={configurationJSON}
          specificationJSON={specificationJSON}
        />
      ) : (
        <Loader overlay />
      )}
    </Page>
  );
};
